import React, { ReactNode } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { toast as sonnerToast } from 'sonner';
import { TbAlertCircle, TbAlertTriangle, TbCircleCheck, TbInfoCircle } from 'react-icons/tb';
import { Close } from '@mui/icons-material';

type ToastStatus = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
    id: string | number;
    title?: ReactNode;
    description?: ReactNode;
    status?: ToastStatus;
}

const statusColor: Record<ToastStatus, string> = {
    success: 'success.main',
    error: 'error.main',
    warning: 'warning.main',
    info: 'info.main',
};
const statusIcon: Record<ToastStatus, React.ReactElement> = {
    success: <TbCircleCheck size={24} />,
    error: <TbAlertCircle size={24} />,
    warning: <TbAlertTriangle size={24} />,
    info: <TbInfoCircle size={24} />,
};

export function toast(toastProps: Omit<ToastProps, 'id'>) {
    return sonnerToast.custom((id) => (
        <Toast
            id={id}
            title={toastProps.title}
            description={toastProps.description}
            status={toastProps.status}
        />
    ));
}

// Extensions for different statuses
export function toastSuccess(props: Omit<ToastProps, 'id' | 'status'>) {
    return toast({ ...props, status: 'success' });
}
export function toastError(props: Omit<ToastProps, 'id' | 'status'>) {
    return toast({ ...props, status: 'error' });
}
export function toastWarning(props: Omit<ToastProps, 'id' | 'status'>) {
    return toast({ ...props, status: 'warning' });
}
export function toastInfo(props: Omit<ToastProps, 'id' | 'status'>) {
    return toast({ ...props, status: 'info' });
}

function Toast(props: ToastProps) {
    const { title: title, description, id, status = 'info' } = props;
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'background.paper',
                boxShadow: 3,
                borderRadius: 2,
                p: 2,
                minWidth: 300,
                maxWidth: 400,
                gap: 2,
            }}
        >
            <Box sx={{ alignSelf: 'flex-start', color: statusColor[status] }}>
                {statusIcon[status]}
            </Box>
            <Box sx={{ flex: 1 }}>
                {typeof title === 'string' ? (
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        color={statusColor[status]}
                    >
                        {title}
                    </Typography>
                ) : (
                    title
                )}
                {description &&
                    (typeof description === 'string' ? (
                        <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            mt={0.5}
                        >
                            {description}
                        </Typography>
                    ) : (
                        description
                    ))}
            </Box>
            <Box sx={{ alignSelf: 'flex-start', mr: 1 }}>
                <IconButton
                    aria-label="close"
                    size="small"
                    onClick={() => sonnerToast.dismiss(id)}
                >
                    <Close fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
}
